import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DayPicker } from '@daypicker/react';
import type { Matcher } from '@daypicker/react';
import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCalendar, IconCalendarCheck } from '@tabler/icons-react';

import styles from './DatePicker.module.css';

// NOTE: `@daypicker/react/style.css` is imported globally from `src/pages/_app.tsx`.
// Next.js 12 (Pages Router) only allows global CSS imports from the custom App,
// so the stylesheet cannot live here even though this is the only consumer.

// We intentionally keep Date values timezone-agnostic: this picker only cares
// about the year/month/day the user chose, not the clock time. Callers that
// need a specific timezone should normalize downstream.
export type DatePickerProps = {
  /** Returns true for days that should be shown as disabled. */
  isOutOfRange: (day: Date) => boolean;
  /**
   * Fires with the chosen day. Always a real Date; the component never
   * reports "no date" through this callback.
   */
  onSelect: (date: Date) => void;
  /**
   * Fires when the user clears the current selection (e.g. taps the
   * already-selected day). Distinct from `onSelect` so consumers don't have
   * to branch on a nullable value.
   */
  onClear: () => void;
  date?: Date;
  initialVisibleMonth?: Date;
  /** DOM id used for the trigger button and aria wiring. */
  id: string;
  placeholder?: string;
  /** Whether this picker is the active choice among sibling controls. */
  isActive?: boolean;
  /** DOM id of a label that describes this control, for screen readers. */
  ariaLabelledBy?: string;
  /** Stretch to the container and move the calendar icon to the trailing edge. */
  fullWidthTrailingIcon?: boolean;
  /** Show the trigger in its validation-failure state. */
  error?: boolean;
  /**
   * Render the calendar a fifth smaller. The offer portal's cards are
   * narrower than checkout's, where the full-size calendar crowds them.
   */
  compactCalendar?: boolean;
};

// MM/DD/YYYY in en-US; cached outside the component so the formatter isn't
// rebuilt on every render.
const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  year: 'numeric',
});

const formatDate = (date: Date): string => dateFormatter.format(date);

/**
 * Single-date picker built on DayPicker. Renders a button that
 * displays the selected date (or a placeholder) and opens a popover calendar
 * on press. The popover closes on selection, Escape, or outside click.
 */
export const DatePicker: React.FC<DatePickerProps> = ({
  isOutOfRange,
  onSelect,
  onClear,
  date,
  initialVisibleMonth,
  id,
  placeholder = 'Select date',
  isActive = false,
  ariaLabelledBy,
  fullWidthTrailingIcon,
  error = false,
  compactCalendar = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarId = `${id}-calendar`;
  const valueId = `${id}-value`;

  // Outside-click / Escape never return focus to the trigger: the user either
  // clicked elsewhere (focus follows their pointer) or is dismissing without a
  // selection, and stealing focus would be surprising in both cases.
  const close = useCallback(() => setIsOpen(false), []);
  // Escape (and day-select) should return focus to the trigger so a keyboard /
  // screen-reader user isn't dropped on <body> when the popover unmounts. We
  // focus before the close so the trigger is still mounted and focusable
  // regardless of React's commit ordering.
  const closeAndReturnFocus = useCallback(() => {
    triggerRef.current?.focus();
    setIsOpen(false);
  }, []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // DayPicker expects its matcher identity to be stable across renders
  // to avoid re-running day-level memoization.
  const disabled = useMemo<Matcher>(() => (day: Date) => isOutOfRange(day), [isOutOfRange]);

  useEffect(() => {
    if (!isOpen) return;
    // `pointerdown` covers both mouse and touch. iOS Safari does not reliably
    // synthesize `mousedown` for taps on non-interactive ancestors, so a
    // mousedown-only listener could strand the popover open on mobile — the
    // exact regression this PR set out to fix.
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAndReturnFocus();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close, closeAndReturnFocus]);

  // DayPicker is the only boundary in this tree that speaks
  // `Date | undefined`. We translate it here into two intent-bearing callbacks
  // so that every consumer downstream works with non-nullable dates.
  const handleSelect = useCallback(
    (selected: Date | undefined) => {
      if (!selected) {
        onClear();
      } else {
        onSelect(selected);
      }
      // Close on both branches: either the user completed a selection or
      // cleared the current one — both are terminal interactions and the
      // popover should reflect that. Return focus to the trigger so the
      // keyboard flow resumes from where it started.
      closeAndReturnFocus();
    },
    [onSelect, onClear, closeAndReturnFocus],
  );

  const displayText = date ? formatDate(date) : placeholder;

  // The three border treatments are mutually exclusive; tailwind-merge cannot
  // arbitrate between token-color classes, so exactly one is ever emitted.
  const triggerBorder = () => {
    if (isActive) return 'border-2 border-od-border-accent-brand';
    if (error) return 'border border-od-border-error';
    return 'border border-od-border-primary';
  };

  // Compose the accessible name. ARIA accessible-name computation is
  // aria-labelledby > aria-label > descendant text, so we must include the
  // selected date in whichever layer actually gets used — otherwise the
  // chosen value is never announced and SR users can't verify their choice
  // before continuing. When an external label is provided, we append the
  // value span's id so SR reads "<label> <date>"; aria-label is kept as a
  // fallback for consumers that don't resolve references, and when no
  // external label exists it inlines the date into aria-label directly.
  let accessibleLabelledBy = ariaLabelledBy;
  if (ariaLabelledBy && date) {
    accessibleLabelledBy = `${ariaLabelledBy} ${valueId}`;
  }
  const accessibleLabel = date ? `${placeholder}, ${displayText}` : placeholder;

  return (
    <Box
      ref={containerRef}
      data-date-picker-active={String(isActive)}
      className={cn('relative inline-flex', fullWidthTrailingIcon && 'w-full')}
    >
      <Box
        ref={triggerRef}
        id={id}
        role="combobox"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        // Only reference the calendar while it exists in the DOM — gating an
        // otherwise-unconditional aria-controls prevents axe-core from
        // flagging a broken ARIA reference when the popover is closed.
        aria-controls={isOpen ? calendarId : undefined}
        aria-labelledby={accessibleLabelledBy}
        aria-label={accessibleLabel}
        analyticsName="buy-direct-checkout-date-picker"
        onClick={toggle}
        // Geometry mirrors the atoms secondary Button used for the "Anytime in
        // <month>" quick-select next to this trigger (h-14, px, rounded-2xl) so
        // the two controls read as a matched pair.
        className={cn(
          'flex h-14 cursor-pointer items-center justify-center rounded-2xl bg-od-bg-primary px-5 transition-colors hover:bg-od-bg-hover-primary',
          triggerBorder(),
          // The trailing-icon form is a form field, not a quick-select button,
          // so it takes the design's input geometry.
          fullWidthTrailingIcon && 'h-[43px] w-full rounded-[12px] border-[1.5px] px-2.5',
        )}
      >
        <Row
          className={cn(
            'items-center gap-2',
            fullWidthTrailingIcon && 'w-full flex-row-reverse justify-between',
          )}
        >
          {fullWidthTrailingIcon ? (
            <IconCalendarCheck size={20} className="text-od-content-primary" />
          ) : (
            <IconCalendar size={20} className="text-od-content-primary" />
          )}
          <Text
            as="span"
            id={valueId}
            // Match the sibling atoms `Button` label exactly: label-md text at
            // the primary content color. The selected-state affordance is
            // carried by the border color, so the label text stays a single
            // color regardless of whether a date has been picked.
            className={cn(
              'text-label-md',
              // An unset date reads as a placeholder, muted like input
              // placeholders; a chosen date is real content.
              date ? 'text-od-content-primary' : 'text-od-content-tertiary',
            )}
          >
            {displayText}
          </Text>
        </Row>
      </Box>

      {isOpen && (
        <Box
          id={calendarId}
          role="dialog"
          aria-modal={false}
          aria-label="Choose date"
          className={cn(
            'absolute top-full left-0 z-[1000] translate-y-2 rounded-lg border border-od-border-primary bg-od-bg-primary shadow-lg',
            // The popup is absolutely positioned, so scaling it moves nothing
            // else; the origin keeps it hanging off the trigger's edge.
            compactCalendar && 'origin-top-left scale-[0.8]',
          )}
        >
          <DayPicker
            mode="single"
            className={styles.dayPicker}
            selected={date}
            defaultMonth={date ?? initialVisibleMonth}
            onSelect={handleSelect}
            disabled={disabled}
            showOutsideDays
            animate
            navLayout="around"
          />
        </Box>
      )}
    </Box>
  );
};
